// =====================================================
// DASHBOARD STATS — Nomenclature du tunnel BPI → PI → ID → PR
// =====================================================
//
// ⚠️ SOURCE DE VÉRITÉ du mapping (établie en lisant le schéma RÉEL de
// desertours-dev `gcgqnhhijbcjzancaekm`, pas deviné). Le disclaimer de la
// maquette dit que les libellés BPI/PI/ID/PR sont des placeholders → ce fichier
// fige les définitions retenues au RUN 1. Ambiguïtés flaggées « FLAG ROBIN ».
//
// | KPI          | Définition réelle                                          |
// |--------------|------------------------------------------------------------|
// | BPI          | form_submissions (generates_bpi) = tous les BPI générés (22/07) |
// | PI           | crews ayant atteint ≥ pré-inscription (statut actif)       |
// | ID           | crews ayant atteint ≥ inscription définitive (statut actif)|
// | PR           | contacts.status='prospect' (GLOBAL, non scopé édition)     |
// | Dérog. attente| crews.derogation_pending=true (« Dérogation en attente ») |
// | Dérog. accept.| crews.has_derogation=true (« Dérogation acceptée »)       |
// | BPI en attente| form_submissions (generates_bpi) converted_at IS NULL     |
//
// Enums réels (pg_enum) :
//   contact_status : prospect, pre_registered, registered, client,
//     bed_incomplete, bed_complete, cancellation_compliant,
//     cancellation_non_compliant, postponement_compliant,
//     postponement_non_compliant, completed, archived
//   crew_status    : bpi_payment_pending, pre_registered, registered,
//     completed, bed_incomplete, bed_complete, cancellation_compliant,
//     cancellation_non_compliant, postponement_compliant,
//     postponement_non_compliant, archived
//   payment_scope  : bpi, bed        payment_status : draft, pending_*, partial, paid, failed, expired

import type { KpiKey, RawCrew } from './types';

/**
 * Statuts crew comptés comme « Pré-inscrit atteint » (PI).
 * Un crew `registered` a forcément été `pre_registered` → inclus (funnel cumul).
 * Exclus : `bpi_payment_pending` (BPI payé mais pré-inscription pas encore
 * validée), `cancellation_*`/`postponement_*` (sortis du tunnel → alimentent le
 * taux d'annulation/report du RUN 4), `archived`.
 */
export const PI_CREW_STATUSES = new Set([
  'pre_registered',
  // 00178 : le crew qui a ouvert son BED reste un pré-inscrit (il ne l'a pas
  // encore soumis) — même position dans le tunnel qu'avant, où il restait
  // `pre_registered`.
  'bed_downloaded',
  'registered',
  'completed',
  'bed_incomplete',
  'bed_incomplete_payment',
  'bed_complete',
]);

/**
 * Statuts crew comptés comme « Inscrit définitif atteint » (ID).
 * bed_incomplete/bed_complete = post-inscription (BED = bulletin d'engagement
 * définitif) → l'équipage est bien un inscrit définitif.
 */
export const ID_CREW_STATUSES = new Set([
  'registered',
  'completed',
  'bed_incomplete',
  // 00178 : « BED incomplet — paiement » est de même nature que
  // « BED incomplet — infos » (bulletin soumis) → même comptage.
  'bed_incomplete_payment',
  'bed_complete',
]);

/** Statut contact comptant comme Prospect (PR). Aligné retour Desertours B :
 * le compteur prospect doit être basé sur le STATUT, pas l'origine. */
export const PR_CONTACT_STATUS = 'prospect';

/**
 * Statuts crew « annulation » (ID résiliées) — RUN 3, carte « Taux édition N ».
 * Un équipage résilié était forcément passé inscrit définitif → il sort du tunnel
 * ID (exclu de ID_CREW_STATUSES) et alimente le taux d'annulation.
 */
export const CANCELLATION_CREW_STATUSES = new Set([
  'cancellation_compliant',
  'cancellation_non_compliant',
]);

/**
 * Statuts crew « report » (équipages basculés sur l'édition suivante) — RUN 3.
 * Sortis du tunnel ID de l'édition courante → alimentent le taux de report.
 */
export const POSTPONEMENT_CREW_STATUSES = new Set([
  'postponement_compliant',
  'postponement_non_compliant',
]);

export function isCancellationCrew(c: RawCrew): boolean {
  return CANCELLATION_CREW_STATUSES.has(c.status);
}

export function isPostponementCrew(c: RawCrew): boolean {
  return POSTPONEMENT_CREW_STATUSES.has(c.status);
}

export function isPiCrew(c: RawCrew): boolean {
  return PI_CREW_STATUSES.has(c.status);
}

export function isIdCrew(c: RawCrew): boolean {
  return ID_CREW_STATUSES.has(c.status);
}

/** Libellés FR des cartes KPI (retouches réunion 22/07). */
export const KPI_LABELS: Record<KpiKey, string> = {
  bpi: 'BPI',
  pi: 'PI équipage',
  id: 'ID équipage',
  pr: 'PR',
  derogations: 'Dérogation en attente',
  derogations_acceptees: 'Dérogation acceptée',
  pi_attente: 'BPI en attente',
};

/** Description longue de chaque carte (survol / sous-titre). */
export const KPI_DESCRIPTIONS: Record<KpiKey, string> = {
  bpi: 'Tous les BPI générés (préinscriptions, payés ou non)',
  pi: 'Pré-inscrits (cumul édition)',
  id: 'Inscrits définitifs (équipage)',
  pr: 'Prospects à date (statut prospect)',
  derogations: 'Dérogations BPI en attente',
  derogations_acceptees: 'Dérogations accordées (équipage)',
  pi_attente: 'BPI en attente de conversion (non payés)',
};

/**
 * FLAG ROBIN — ambiguïtés de nomenclature à confirmer avec Desertours :
 *  1. PI = interprété « cumul des crews ayant atteint la pré-inscription »
 *     (statut actif). Si Desertours veut le compte des crews ACTUELLEMENT au
 *     stade `pre_registered` seul, restreindre PI_CREW_STATUSES à
 *     {'pre_registered'}.
 *  2. « PI en attente » (6e carte, vue dans le screenshot) = interprété
 *     `form_submissions.converted_at IS NULL` sur les formulaires generates_bpi.
 *     Alternative possible = crews `bpi_payment_pending`. À confirmer.
 *  3. PR (prospects) = non scopé par édition (un prospect n'a pas encore de
 *     produit). Compté globalement → pas de N-1 par édition (affiche N/A).
 *  4. Dérogations = `derogation_pending` (« en attente »). `has_derogation`
 *     (dérogations accordées, total) existe aussi si un 2e compteur est voulu.
 */
export const NOMENCLATURE_FLAGS = [
  'PI = crews ayant atteint ≥ pre_registered (cumul). Restreindre à {pre_registered} si stade courant voulu.',
  'PI en attente = form_submissions.converted_at IS NULL (generates_bpi). Alternative : crews bpi_payment_pending.',
  'PR = contacts.status=prospect, GLOBAL (non scopé édition) → N-1 par édition = N/A.',
  'Dérogations = derogation_pending (en attente). has_derogation dispo pour un total.',
] as const;
